import axios from 'axios';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Palette, MapPin, Home, TreePine } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export default function PresentationClient() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [codes, setCodes] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadWithRetry = async (retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          const res = await axios.get(`${API_URL}/api/crm/presentation/${token}`);
          if (res.status === 200) {
            const d = res.data;
            setData(d);
            const initial = {};
            d.produits.forEach(p => { initial[p.index] = p.code_couleur || ''; });
            setCodes(initial);
            setLoading(false); return;
          }
          if ((res.status === 404 || res.status >= 500) && i < retries - 1) { await new Promise(r => setTimeout(r, 2500)); continue; }
          setError('Lien invalide'); setLoading(false); return;
        } catch (e) {
          if (i < retries - 1) { await new Promise(r => setTimeout(r, 2500)); continue; }
          setError(e.message); setLoading(false);
        }
      }
    };
    loadWithRetry();
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/presentation/${token}/codes-couleur`, { codes_couleur: codes });
      if (res.status !== 200) throw new Error('Erreur');
      setSubmitted(true);
      toast.success('Codes couleur enregistrés!');
    } catch (e) { toast.error('Erreur lors de la soumission'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="py-12 text-center">
          <p className="text-red-500 text-lg font-medium mb-2">{error}</p>
          <p className="text-gray-500">Ce lien est invalide ou a expiré.</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="py-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Merci!</h2>
          <p className="text-gray-600">Vos codes couleur ont bien été enregistrés. Notre équipe va préparer votre chantier en conséquence.</p>
        </CardContent>
      </Card>
    </div>
  );

  const isExt = data.type_travaux === 'exterieur';
  const TypeIcon = isExt ? TreePine : Home;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4" data-testid="presentation-client-page">
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Peinture Résidentielle QC</h1>
          <p className="text-gray-500">Présentation de vos travaux de peinture</p>
        </div>

        {/* Infos projet */}
        <Card>
          <CardContent className="py-5">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${isExt ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                <TypeIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-semibold text-lg">{data.dossier?.nom_dossier || 'Votre projet'}</h2>
                <p className="text-sm text-gray-500">{data.dossier?.client_nom}</p>
              </div>
              <Badge className="ml-auto">{isExt ? 'Extérieur' : 'Intérieur'}</Badge>
            </div>
            {data.dossier?.adresse_travaux && (
              <p className="text-sm text-gray-600 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                {data.dossier.adresse_travaux}, {data.dossier.ville_travaux}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Saisie des codes couleur */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="w-5 h-5 text-blue-600" />
              Vos codes couleur
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Veuillez saisir ou confirmer les codes couleur pour chaque produit. Exemple : OC-17, BM-2163-40, etc.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.produits.map((p) => (
                <div key={p.index} className="border rounded-lg p-4 bg-gray-50" data-testid={`produit-row-${p.index}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{p.produit_nom}</p>
                      <div className="flex gap-3 text-xs text-gray-500 mt-1">
                        {p.fini && <span>Fini: {p.fini}</span>}
                        {p.emplacement && <span>{isExt ? 'Surface' : 'Pièce'}: {p.emplacement}</span>}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Code couleur</label>
                    <Input
                      value={codes[p.index] || ''}
                      onChange={(e) => setCodes(c => ({ ...c, [p.index]: e.target.value }))}
                      placeholder="Ex: OC-17 ou BM-2163-40"
                      className="bg-white"
                      data-testid={`code-couleur-input-${p.index}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-center">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 px-8"
                data-testid="btn-soumettre-codes"
              >
                {submitting ? 'Enregistrement...' : 'Enregistrer mes codes couleur'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-400 mt-8">
          Peinture Résidentielle QC &mdash; Tous droits réservés
        </p>
      </div>
    </div>
  );
}
